import { useState } from "react";
import { Plus, Mail, Trash2 } from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { estApi } from "../lib/api";
import { ContactCell } from "@/components/ContactCell";

const inputCls = "mt-1 h-8 w-full rounded-md border border-slate-200 px-2 text-xs";
const ROLES = ["gc", "owner", "architect", "other"];
const uid = () => (crypto.randomUUID ? crypto.randomUUID() : String(Math.random()));

function AddContactDialog({ open, onOpenChange, companies, defaultCompanyId, onCreated }) {
  const [form, setForm] = useState({
    first_name: "", last_name: "", email: "", phone: "", title: "",
    company_id: defaultCompanyId || "",
  });
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    try {
      const c = await estApi.createContact({ ...form, company_id: form.company_id || null });
      toast.success(`${c.display_name} added`);
      onCreated(c);
      onOpenChange(false);
      setForm({ first_name: "", last_name: "", email: "", phone: "", title: "", company_id: defaultCompanyId || "" });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not add contact");
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="add-contact-dialog">
        <DialogHeader>
          <DialogTitle>Add Contact</DialogTitle>
          <DialogDescription>Shared contact list — used for proposal emails later.</DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <label className="block">
            <span className="text-[11px] font-medium uppercase tracking-wide text-slate-500">First name</span>
            <input value={form.first_name} onChange={(e) => set("first_name", e.target.value)}
                   className={inputCls} data-testid="contact-first-name-input" />
          </label>
          <label className="block">
            <span className="text-[11px] font-medium uppercase tracking-wide text-slate-500">Last name</span>
            <input value={form.last_name} onChange={(e) => set("last_name", e.target.value)}
                   className={inputCls} data-testid="contact-last-name-input" />
          </label>
        </div>
        <label className="block">
          <span className="text-[11px] font-medium uppercase tracking-wide text-slate-500">Email</span>
          <input value={form.email} onChange={(e) => set("email", e.target.value)}
                 className={inputCls} data-testid="contact-email-input" />
        </label>
        <div className="grid grid-cols-2 gap-3">
          <label className="block">
            <span className="text-[11px] font-medium uppercase tracking-wide text-slate-500">Phone</span>
            <input value={form.phone} onChange={(e) => set("phone", e.target.value)}
                   className={inputCls} data-testid="contact-phone-input" />
          </label>
          <label className="block">
            <span className="text-[11px] font-medium uppercase tracking-wide text-slate-500">Title</span>
            <input value={form.title} onChange={(e) => set("title", e.target.value)}
                   className={inputCls} data-testid="contact-title-input" />
          </label>
        </div>
        <label className="block">
          <span className="text-[11px] font-medium uppercase tracking-wide text-slate-500">Company</span>
          <select value={form.company_id} onChange={(e) => set("company_id", e.target.value)}
                  className={inputCls} data-testid="contact-company-input">
            <option value="">— none —</option>
            {companies.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
        </label>
        <DialogFooter>
          <Button variant="outline" size="sm" onClick={() => onOpenChange(false)}
                  data-testid="contact-cancel-btn">Cancel</Button>
          <Button size="sm" onClick={save} data-testid="contact-save-btn"
                  disabled={!form.first_name && !form.last_name && !form.email}>
            Add contact
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/**
 * Contacts attached to the estimate. The dropdown is filtered to the selected GC(s);
 * each row has an "email proposal to" toggle used by Phase 6.
 */
export default function ContactPicker({ links = [], contacts, companies, companyIds, onChange, onContactAdded, onContactUpdated, onEmail }) {
  const [adding, setAdding] = useState(false);

  const attachedIds = links.map((l) => l.contact_id);
  const pool = contacts.filter((c) => !attachedIds.includes(c.id));
  const scoped = companyIds?.length
    ? pool.filter((c) => companyIds.includes(c.company_id))
    : pool;

  const add = (contactId) => {
    const rec = contacts.find((c) => c.id === contactId);
    onChange([...links, {
      id: uid(), contact_id: contactId,
      role: rec?.company_id && companyIds?.includes(rec.company_id) ? "gc" : "other",
      is_email_recipient: true,
    }]);
  };

  const patch = (id, changes) =>
    onChange(links.map((l) => (l.id === id ? { ...l, ...changes } : l)));

  const detail = (link) => contacts.find((c) => c.id === link.contact_id) || {};

  const saveContact = async (c, d) => {
    const [first_name = "", ...rest] = d.name.split(" ");
    try {
      const upd = await estApi.updateContact(c.id, { first_name, last_name: rest.join(" "), email: d.email, phone: d.phone });
      onContactUpdated?.({ ...c, ...upd, display_name: d.name || d.email });
      toast.success("Contact updated");
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not update contact"); }
  };

  return (
    <div data-testid="contact-picker">
      <div>
        <table className="w-full text-[11px]">
          <thead className="border-b border-slate-200">
            <tr>
              {["Company", "Contact", "Role", "Email proposal", ""].map((h) => (
                <th key={h} className="whitespace-nowrap px-2 py-1.5 text-left font-medium text-slate-500">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {links.length === 0 && (
              <tr><td colSpan={5} className="px-2 py-4 text-center text-slate-400">
                No contacts attached yet.
              </td></tr>
            )}
            {links.map((l) => {
              const c = detail(l);
              const name = c.display_name || `${c.first_name || ""} ${c.last_name || ""}`.trim();
              return (
                <tr key={l.id || l.contact_id} className="border-b border-slate-100 last:border-0" data-testid={`contact-row-${l.contact_id}`}>
                  <td className="px-2 py-1.5 text-slate-600">{c.company_name || "—"}</td>
                  <td className="px-2 py-1.5">
                    <div className="flex items-center gap-2">
                      <div className="min-w-0 flex-1">
                        <ContactCell name={name} email={c.email || ""} phone={c.phone || ""}
                                     onSave={(d) => saveContact(c, d)} testid={`contact-cell-${l.contact_id}`} />
                      </div>
                      {onEmail && c.email && (
                        <Button variant="data" size="sm" className="h-6 shrink-0 px-2 text-[11px]"
                                onClick={() => onEmail(c)} title={`Email the proposal to ${c.email}`}
                                data-testid={`contact-email-proposal-${l.contact_id}`}>
                          <Mail className="mr-1 h-3 w-3" /> Send
                        </Button>
                      )}
                    </div>
                  </td>
                  <td className="px-2 py-1.5">
                    <select value={l.role} onChange={(e) => patch(l.id, { role: e.target.value })}
                            className="h-6 rounded border border-slate-200 px-1 text-[11px]"
                            data-testid={`contact-role-${l.contact_id}`}>
                      {ROLES.map((r) => <option key={r} value={r}>{r}</option>)}
                    </select>
                  </td>
                  <td className="px-2 py-1.5">
                    <input type="checkbox" checked={!!l.is_email_recipient}
                           onChange={(e) => patch(l.id, { is_email_recipient: e.target.checked })}
                           disabled={!c.email}
                           title={c.email ? "" : "This contact has no email address"}
                           data-testid={`contact-email-toggle-${l.contact_id}`} />
                  </td>
                  <td className="px-1 py-1.5">
                    <button onClick={() => onChange(links.filter((x) => x.id !== l.id))}
                            className="text-slate-300 hover:text-red-500"
                            data-testid={`remove-contact-${l.contact_id}`}>
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div className="mt-2 flex gap-2">
        <select value="" onChange={(e) => e.target.value && add(e.target.value)}
                className="h-8 flex-1 rounded-md border border-slate-200 px-2 text-xs"
                data-testid="contact-select">
          <option value="">
            {companyIds?.length ? "+ Attach a contact from this customer…" : "+ Attach a contact…"}
          </option>
          {scoped.map((c) => (
            <option key={c.id} value={c.id}>
              {c.display_name}{c.company_name ? ` — ${c.company_name}` : ""}
            </option>
          ))}
        </select>
        <Button variant="outline" size="sm" onClick={() => setAdding(true)} data-testid="add-contact-btn">
          <Plus className="h-3.5 w-3.5 mr-1" /> Add Contact
        </Button>
      </div>
      {companyIds?.length > 0 && scoped.length === 0 && pool.length > 0 && (
        <p className="mt-1 text-[10px] text-amber-600">
          No contacts on this customer yet — add one, or clear the customer selection to see all contacts.
        </p>
      )}

      <AddContactDialog open={adding} onOpenChange={setAdding} companies={companies}
                        defaultCompanyId={companyIds?.[0]}
                        onCreated={(c) => { onContactAdded(c); add(c.id); }} />
    </div>
  );
}
